import React, { useId } from 'react';
import {
  alpha,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  type PaperProps,
  useTheme,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  borderRadius,
  componentSizes,
  getSemanticSurfaceTokens,
} from '../../../theme/theme';
import { BodyText, CaptionText, CompactPageTitle } from '../typography';

export interface ConfirmationDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmColor?: 'primary' | 'error' | 'warning' | 'success';
  destructive?: boolean;
  cancelAriaLabel?: string;
  confirmAriaLabel?: string;
  detailsLabel?: string;
  details?: readonly ConfirmationDialogDetail[];
  onCancel: () => void;
  onConfirm: () => void;
  'aria-labelledby'?: string;
}

export interface ConfirmationDialogDetail {
  title: string;
  description: React.ReactNode;
  emphasized?: boolean;
}

/**
 * Shared app-styled confirmation dialog. Visual language matches the rest of
 * the app's dialogs (ProfileSaveAsDialog et al.): lg radius paper with a
 * strong border, compact header with a light divider, small radius-sm action
 * buttons. Destructive/warning confirmations get a leading status icon.
 */
export const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  title,
  message,
  cancelLabel = 'Cancel',
  confirmLabel = 'Confirm',
  confirmColor,
  destructive = false,
  cancelAriaLabel,
  confirmAriaLabel,
  detailsLabel,
  details,
  onCancel,
  onConfirm,
  'aria-labelledby': ariaLabelledBy,
}) => {
  const theme = useTheme();
  const colors = getSemanticSurfaceTokens(theme);
  const fallbackTitleId = useId();
  const titleId = ariaLabelledBy ?? fallbackTitleId;
  const color = destructive ? 'error' : confirmColor;
  const emphasized = destructive || confirmColor !== undefined;
  const warning = color === 'warning';
  const danger = destructive || color === 'error';
  const alerting = warning || danger;
  const tonePalette = warning
    ? theme.palette.warning
    : danger
      ? theme.palette.error
      : null;
  const messageParagraphs = typeof message === 'string'
    ? message.split(/\n\s*\n/).filter(Boolean)
    : null;

  const statusIcon = destructive || color === 'error'
    ? <ErrorOutlineIcon color="error" fontSize="small" />
    : color === 'warning'
      ? <WarningAmberIcon color="warning" fontSize="small" />
      : null;

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-labelledby={titleId}
      maxWidth={details?.length ? 'sm' : 'xs'}
      fullWidth
      PaperComponent={alerting ? AlertDialogPaper : undefined}
      PaperProps={{
        // Interrupting confirmations must stay alertdialog per ARIA; tests
        // querying these dialogs need findByRole('alertdialog'), not 'dialog'.
        role: alerting ? 'alertdialog' : 'dialog',
        sx: {
          borderRadius: `${borderRadius.lg}px`,
          border: `1px solid ${
            tonePalette ? alpha(tonePalette.main, 0.58) : colors.border.strong
          }`,
          bgcolor: theme.palette.background.paper,
          backgroundImage: 'none',
          boxShadow: tonePalette
            ? `0 18px 42px ${alpha(theme.palette.common.black, 0.25)}, 0 0 0 1px ${alpha(
                tonePalette.main,
                0.12
              )}`
            : `0 18px 42px ${alpha(theme.palette.common.black, 0.22)}`,
          overflow: 'hidden',
        },
      }}
    >
      <DialogTitle
        id={titleId}
        sx={{
          px: 2,
          pt: alerting ? 1.35 : 1.65,
          pb: alerting ? 1.15 : 0.9,
          borderBottom: `1px solid ${
            tonePalette ? alpha(tonePalette.main, 0.28) : colors.border.light
          }`,
          backgroundColor: tonePalette
            ? alpha(tonePalette.main, theme.palette.mode === 'light' ? 0.11 : 0.14)
            : 'transparent',
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {statusIcon ? (
          <DialogStatusIcon toneColor={tonePalette?.main}>{statusIcon}</DialogStatusIcon>
        ) : null}
        <span>
          <CompactPageTitle component="span" sx={{ color: colors.text.primary }}>
            {title}
          </CompactPageTitle>
        </span>
      </DialogTitle>
      <DialogContent
        sx={{
          px: 2,
          pb: 1.35,
          backgroundColor: 'transparent',
          // MUI zeroes DialogContent top padding right after a DialogTitle via
          // `.MuiDialogTitle-root + &`, which outranks a plain `pt`; match its
          // specificity so the top padding equals the horizontal padding.
          '&.MuiDialogContent-root': { pt: 2 },
        }}
      >
        {messageParagraphs ? (
          <Box sx={{ display: 'grid', gap: 1.15 }}>
            {messageParagraphs.map((paragraph, index) => (
              <BodyText
                key={`${index}-${paragraph}`}
                secondary={!alerting}
                sx={{
                  color: alerting ? colors.text.primary : colors.text.secondary,
                }}
              >
                {paragraph}
              </BodyText>
            ))}
          </Box>
        ) : (
          <BodyText
            secondary={!alerting}
            sx={{ color: alerting ? colors.text.primary : colors.text.secondary }}
          >
            {message}
          </BodyText>
        )}

        {details?.length ? (
          <Box component="details" open sx={{ mt: 1.6 }}>
            <CaptionText
              component="summary"
              sx={{
                display: 'list-item',
                mb: 0.7,
                color: colors.text.secondary,
                fontWeight: 800,
                letterSpacing: '0.045em',
                textTransform: 'uppercase',
                cursor: 'pointer',
                '&:focus-visible': {
                  outline: `2px solid ${theme.palette.primary.main}`,
                  outlineOffset: 2,
                  borderRadius: `${borderRadius.sm}px`,
                },
              }}
            >
              {detailsLabel ?? 'What this choice means'}
            </CaptionText>
            <Box
              component="ul"
              aria-label={detailsLabel ?? 'What this choice means'}
              sx={{
                display: 'grid',
                gap: 0.45,
                m: 0,
                p: 0,
                listStyle: 'none',
              }}
            >
              {details.map((detail, index) => (
                <Box
                  component="li"
                  key={`${index}-${detail.title}`}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: detail.emphasized
                      ? '4px minmax(0, 1fr)'
                      : 'minmax(0, 1fr)',
                    gap: detail.emphasized ? 1 : 0,
                    px: detail.emphasized ? 1 : 0.25,
                    py: detail.emphasized ? 1 : 0.7,
                    borderRadius: detail.emphasized ? `${borderRadius.md}px` : 0,
                    border: detail.emphasized
                      ? `1px solid ${
                          tonePalette ? alpha(tonePalette.main, 0.5) : colors.border.strong
                        }`
                      : 'none',
                    borderBottom: !detail.emphasized && index < details.length - 1
                      ? `1px solid ${colors.border.light}`
                      : undefined,
                    backgroundColor: detail.emphasized && tonePalette
                      ? alpha(tonePalette.main, theme.palette.mode === 'light' ? 0.08 : 0.1)
                      : 'transparent',
                  }}
                >
                  {detail.emphasized ? (
                    <Box
                      component="span"
                      aria-hidden
                      sx={{
                        width: 4,
                        alignSelf: 'stretch',
                        minHeight: 38,
                        borderRadius: `${borderRadius.sm}px`,
                        backgroundColor: tonePalette?.main ?? alpha(theme.palette.text.primary, 0.3),
                      }}
                    />
                  ) : null}
                  <Box
                    role={detail.emphasized ? 'note' : undefined}
                    aria-label={detail.emphasized ? detail.title : undefined}
                    sx={{ display: 'grid', gap: 0.2, minWidth: 0 }}
                  >
                    <CaptionText
                      sx={{
                        color: detail.emphasized
                          ? tonePalette?.main ?? colors.text.primary
                          : colors.text.primary,
                        fontWeight: 800,
                      }}
                    >
                      {detail.title}
                    </CaptionText>
                    <BodyText sx={{ color: colors.text.secondary, lineHeight: 1.4 }}>
                      {detail.description}
                    </BodyText>
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions
        sx={{
          px: 2,
          py: 1.25,
          gap: 0.5,
          borderTop: `1px solid ${colors.border.light}`,
          backgroundColor: 'transparent',
        }}
      >
        <Button
          size="small"
          onClick={onCancel}
          color="inherit"
          variant="outlined"
          aria-label={cancelAriaLabel}
          sx={{ minHeight: 30, borderRadius: `${borderRadius.sm}px` }}
        >
          {cancelLabel}
        </Button>
        <Button
          size="small"
          onClick={onConfirm}
          color={color ?? 'primary'}
          variant={emphasized ? 'contained' : 'text'}
          autoFocus={emphasized}
          aria-label={confirmAriaLabel}
          sx={{
            minHeight: 30,
            borderRadius: `${borderRadius.sm}px`,
            ...(warning
              ? {
                  color: theme.palette.getContrastText(theme.palette.warning.main),
                  backgroundColor: theme.palette.warning.main,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.warning.dark,
                    boxShadow: 'none',
                  },
                }
              : {}),
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const AlertDialogPaper = React.forwardRef<HTMLDivElement, PaperProps>((props, ref) => (
  <Paper {...props} ref={ref} />
));

AlertDialogPaper.displayName = 'AlertDialogPaper';

const DialogStatusIcon: React.FC<{ toneColor?: string; children: React.ReactNode }> = ({
  toneColor,
  children,
}) => (
  <Box
    component="span"
    sx={{
      width: componentSizes.iconButton,
      height: componentSizes.iconButton,
      flex: '0 0 auto',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '50%',
      color: toneColor,
      backgroundColor: toneColor ? alpha(toneColor, 0.14) : 'transparent',
    }}
  >
    {children}
  </Box>
);
